import React from 'react';
import { 
  Box, Container, Typography, Button, Paper, Divider, 
  List, ListItem, ListItemIcon, ListItemText, Grid, Stack, Alert, AlertTitle
} from '@mui/material';
import { 
  CurrencyExchange, CheckCircle, Info, AssignmentReturn, 
  Cancel, HourglassBottom, ReceiptLong, WarningAmber, 
  Gavel, Email, SupportAgent, Refresh, AccountBalanceWallet
} from '@mui/icons-material';

export default function RefundPolicy() {
  const primaryGradient = 'linear-gradient(135deg, #FF6A00 0%, #EE0979 100%)';
  const darkBg = '#070B14';
  const lightBg = '#f8fafc';

  // Helper component for styled lists
  const CustomListItem = ({ text, icon = <CheckCircle sx={{ color: '#FF6A00', fontSize: 18 }} /> }) => (
    <ListItem sx={{ py: 0.5, px: 0 }}>
      <ListItemIcon sx={{ minWidth: 32 }}>
        {icon}
      </ListItemIcon>
      <ListItemText primary={text} primaryTypographyProps={{ color: '#475569', fontSize: '1rem', lineHeight: 1.5 }} />
    </ListItem>
  );

  // Helper component for section headers
  const SectionHeader = ({ icon, title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 5 }}>
      <Box 
        sx={{ 
          width: 40, height: 40, borderRadius: '10px', 
          background: 'rgba(255,106,0,0.1)', color: '#FF6A00',
          display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2,
          flexShrink: 0
        }}
      >
        {icon}
      </Box>
      <Typography variant="h5" fontWeight="800" color="#1E293B">
        {title}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: lightBg, minHeight: '100vh', pb: 10 }}>
      
      {/* 🌟 PAGE HEADER 🌟 */}
      <Box sx={{ position: 'relative', pt: { xs: 12, md: 16 }, pb: { xs: 8, md: 10 }, bgcolor: darkBg, overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '60%', height: '2px', background: primaryGradient, boxShadow: '0 0 20px 5px rgba(255, 81, 47, 0.4)' }} />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <CurrencyExchange sx={{ fontSize: 50, color: '#FF6A00', mb: 2 }} />
          <Typography variant="h2" fontWeight="900" color="white" sx={{ mb: 2, letterSpacing: '-1px', fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            Refund Policy
          </Typography>
          <Typography variant="h6" color="#94A3B8" fontWeight="500">
            Last updated: January 2025
          </Typography>
        </Container>
      </Box>

      {/* 🌟 MAIN CONTENT 🌟 */}
      <Container maxWidth="md" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2 }}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 3, md: 6 }, 
            borderRadius: 4, 
            border: '1px solid rgba(0,0,0,0.05)',
            boxShadow: '0 10px 40px rgba(0,0,0,0.03)'
          }}
        >
          
          <SectionHeader icon={<Info />} title="1. Overview" />
          <Typography variant="body1" color="#475569" lineHeight={1.8}>
            At <strong>PracharApp</strong>, we strive to provide the best possible service to our users. This Refund Policy outlines the circumstances under which refunds may be provided and the process for requesting them.
            <br/><br/>
            By purchasing any of our services, you agree to the terms outlined in this policy.
          </Typography>

          {/* 🌟 SPECIAL GUARANTEE BOX 🌟 */}
          <Box sx={{ mt: 5, mb: 4 }}>
            <Alert 
              icon={<AccountBalanceWallet fontSize="inherit" sx={{ color: '#059669' }} />} 
              severity="success"
              sx={{ 
                border: '1px solid #34d399', 
                bgcolor: '#ecfdf5', 
                borderRadius: 3, 
                p: 3,
                '& .MuiAlert-icon': { fontSize: '2.5rem', mt: 0.5 }
              }}
            >
              <AlertTitle sx={{ fontWeight: '800', fontSize: '1.2rem', color: '#065f46', mb: 1 }}>
                30-Day Money-Back Guarantee
              </AlertTitle>
              <Typography variant="body2" color="#064e3b" lineHeight={1.6}>
                We offer a 30-day money-back guarantee for all premium subscriptions. You may request a full refund within 30 days of your initial purchase if you are not satisfied, the service does not meet expectations, or you experience unresolved technical issues.
              </Typography>
            </Alert>
          </Box>

          <SectionHeader icon={<AssignmentReturn />} title="2. Refund Eligibility" />
          <Typography variant="subtitle1" fontWeight="700" color="#1E293B" mb={1}>Conditions for Refund</Typography>
          <Typography variant="body1" color="#475569" mb={1} lineHeight={1.8}>
            To be eligible for a refund, the following conditions must be met:
          </Typography>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6}><CustomListItem text="Request made within 30 days of purchase" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="No violation of Terms of Service" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="No abuse or misuse of the service" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Valid reason provided for the request" /></Grid>
          </Grid>

          <SectionHeader icon={<Cancel />} title="3. Non-Refundable Items" />
          <Typography variant="body1" color="#475569" mb={1} lineHeight={1.8}>
            The following items are <strong>not</strong> eligible for refunds:
          </Typography>
          <List disablePadding>
            <CustomListItem icon={<Cancel sx={{ color: '#ef4444', fontSize: 18 }} />} text="Subscriptions active for more than 30 days" />
            <CustomListItem icon={<Cancel sx={{ color: '#ef4444', fontSize: 18 }} />} text="One-time purchases of individual templates or assets" />
            <CustomListItem icon={<Cancel sx={{ color: '#ef4444', fontSize: 18 }} />} text="Custom design services (once work has commenced)" />
            <CustomListItem icon={<Cancel sx={{ color: '#ef4444', fontSize: 18 }} />} text="Promotional or discounted subscriptions (unless specified)" />
            <CustomListItem icon={<Cancel sx={{ color: '#ef4444', fontSize: 18 }} />} text="Accounts suspended for policy violations" />
          </List>

          <SectionHeader icon={<ReceiptLong />} title="4. Refund Process" />
          <Typography variant="subtitle1" fontWeight="700" color="#1E293B" mb={1}>How to Request a Refund</Typography>
          <List disablePadding sx={{ mb: 3 }}>
            <CustomListItem icon={<Typography fontWeight="bold" color="#FF6A00">1.</Typography>} text="Contact our support team at refunds@pracharapp.com" />
            <CustomListItem icon={<Typography fontWeight="bold" color="#FF6A00">2.</Typography>} text="Include your account email and transaction ID" />
            <CustomListItem icon={<Typography fontWeight="bold" color="#FF6A00">3.</Typography>} text="Provide a clear reason for your refund request" />
            <CustomListItem icon={<Typography fontWeight="bold" color="#FF6A00">4.</Typography>} text="Allow up to 5 business days for our team to review" />
          </List>

          <Typography variant="subtitle1" fontWeight="700" color="#1E293B" mb={1}>Processing Time</Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}><CustomListItem text="Credit card refunds: 5-10 business days" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="PayPal refunds: 3-5 business days" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Bank transfer refunds: 7-14 business days" /></Grid>
            <Grid item xs={12} sm={6}><CustomListItem text="Digital wallet refunds: 1-3 business days" /></Grid>
          </Grid>

          <SectionHeader icon={<Refresh />} title="5. Partial Refunds" />
          <Typography variant="body1" color="#475569" mb={2} lineHeight={1.8}>
            In certain circumstances, we may offer partial refunds on a pro-rata basis:
          </Typography>
          <List disablePadding>
            <CustomListItem text="If you've used a significant portion of your subscription period" />
            <CustomListItem text="For technical issues that affected only part of your service period" />
            <CustomListItem text="When downgrading from a higher-tier plan" />
          </List>

          <SectionHeader icon={<WarningAmber />} title="6. Subscription Cancellations" />
          <Typography variant="body1" color="#475569" mb={2} lineHeight={1.8}>
            You can cancel your subscription at any time through your account settings. Please note:
          </Typography>
          <List disablePadding>
            <CustomListItem text="Cancellation stops future billing but doesn't automatically trigger a refund" />
            <CustomListItem text="You retain access to premium features until the billing period ends" />
            <CustomListItem text="Refunds for cancelled subscriptions are subject to our 30-day policy" />
            <CustomListItem text="No refunds are provided for the current billing period after 30 days" />
          </List>

          <SectionHeader icon={<Gavel />} title="7. Dispute & Chargeback Policy" />
          <Typography variant="body1" color="#475569" mb={2} lineHeight={1.8}>
            We strongly encourage you to contact us directly before initiating a chargeback with your bank. Chargebacks can result in immediate account suspension and additional administrative fees. We are committed to resolving disputes amicably.
          </Typography>

        </Paper>
      </Container>

      {/* 🌟 BOTTOM CTA SECTION (SPLIT BUTTONS) 🌟 */}
      <Container maxWidth="md" sx={{ mt: 6 }}>
        <Box 
          sx={{ 
            background: primaryGradient, borderRadius: 4, p: { xs: 4, md: 5 }, 
            textAlign: 'center', color: 'white', boxShadow: '0 10px 20px rgba(238, 9, 121, 0.2)'
          }}
        >
          <SupportAgent sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
          <Typography variant="h4" fontWeight="900" mb={1}>Need Help with a Refund?</Typography>
          <Typography variant="subtitle1" mb={4} sx={{ opacity: 0.9 }}>
            Our support team is here to assist you with any refund-related questions.
          </Typography>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button 
              component="a" href="mailto:refunds@pracharapp.com"
              variant="contained" startIcon={<CurrencyExchange />}
              sx={{ 
                bgcolor: 'white', color: '#EE0979', px: 4, py: 1.5, 
                borderRadius: '50px', fontWeight: '800', fontSize: '1rem',
                '&:hover': { bgcolor: '#f8fafc', transform: 'translateY(-2px)' },
                transition: 'all 0.3s'
              }}
            >
              Request Refund
            </Button>
            <Button 
              component="a" href="mailto:support@pracharapp.com"
              variant="outlined" startIcon={<Email />}
              sx={{ 
                borderColor: 'rgba(255,255,255,0.6)', color: 'white', px: 4, py: 1.5, 
                borderRadius: '50px', fontWeight: '800', fontSize: '1rem',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)', transform: 'translateY(-2px)' },
                transition: 'all 0.3s'
              }}
            >
              Contact Support
            </Button>
          </Stack>
        </Box>
      </Container>

    </Box>
  );
}